/**
 * Stored-vs-recomputed pour volume comparison for the stope volume table.
 *
 * WHY THIS EXISTS. The volumes stored on existing pours were measured with CSG against stope meshes that
 * were half-degenerate (see geometryRepair), and can be far out - up to ~42% on a first slice. Nothing is
 * recomputed automatically: "Update Volumes" is deliberately an opt-in action. Without an indicator an
 * engineer has no way to tell which stopes still carry those stale figures, so this flags the ones where
 * pressing that button would actually change something.
 */

// Matches the threshold "Update Volumes" itself uses before it PATCHes a pour, so the indicator lights up
// on exactly the pours that action would rewrite - no more, no less.
export const MATERIAL_VOLUME_DELTA_M3 = 0.5;

export interface PourVolumeComparison {
  /** recomputed - stored, in cubic metres. Positive means the pour actually holds more than recorded. */
  difference: number;
  /** Signed fraction of the stored volume, or undefined when the stored volume is zero. */
  relative: number | undefined;
  isMaterial: boolean;
}

export const compareStoredVolume = (
  storedVolume: number | undefined | null,
  recomputedVolume: number | undefined | null,
): PourVolumeComparison | null => {
  if (typeof storedVolume !== 'number' || !Number.isFinite(storedVolume)) return null;
  if (typeof recomputedVolume !== 'number' || !Number.isFinite(recomputedVolume)) return null;

  const difference = recomputedVolume - storedVolume;

  return {
    difference,
    relative: storedVolume > 0 ? difference / storedVolume : undefined,
    isMaterial: Math.abs(difference) > MATERIAL_VOLUME_DELTA_M3,
  };
};

/** A signed, rounded label such as "+42" or "-7" for display next to the stored volume. */
export const formatVolumeDelta = (difference: number): string => {
  const rounded = Math.round(difference);
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
};

/** A signed percentage label such as "+3.6%". Empty when there is no meaningful denominator. */
export const formatVolumeDeltaPercent = (relative: number | undefined): string => {
  if (relative === undefined) return '';
  const percent = relative * 100;
  return `${percent > 0 ? '+' : ''}${percent.toFixed(1)}%`;
};
